// M1 achievements. check(state) must be pure and cheap (called once per tick until unlocked).
// Fields read from sim state (sim agent: please maintain these):
//   well.status, well.prod, well.payFt, well.spudDay, well.depth, well.target, well.fieldId, well.plot
//   well.gushed = true     once a well has gushed (cable-tool GUSHER state)
//   well.minWob            lowest wob used while DRILLING this well (DESIGN §14)
//   well.fishingJobs       count of fishing jobs on this well
//   company.stats.dryStreak (optional; otherwise derived from wells)
import { ownWells } from './achWells.js';
import { itemPrice } from './equipment.js';
import { dayToDate } from '../core/time.js';
import { M2_ACHIEVEMENTS } from './m2/achievements.js';
import { M3_ACHIEVEMENTS } from './m3/achievements.js';
import { M4_ACHIEVEMENTS } from './m4/achievements.js';
import { m4List } from './m4/gate.js';

const wells = ownWells; // Quick Start wells don't count (polish G7); shared per tick (achWells.js)
const isProducer = (w) => !!w.prod || w.status === 'PRODUCING' || w.status === 'COMPLETING';
const isDry = (w) => w.status === 'DRY' || (w.status === 'PLUGGED' && !w.prod && !(w.payFt > 0));

export const ACHIEVEMENTS = [
  { id: 'drakes_folly', name: "Drake's Folly", icon: 'derrick',
    desc: 'Strike oil with your first producing well. They called it a folly too.',
    check: (s) => wells(s).some(isProducer) },
  { id: 'gusher', name: 'Gusher!', icon: 'gusher',
    desc: 'Bring in a well that blows oil over the crown block.',
    check: (s) => wells(s).some((w) => w.gushed || w.status === 'GUSHER') },
  { id: 'witched', name: 'Witched', icon: 'twig',
    desc: 'Strike oil on a plot surveyed only by a dowser.',
    check: (s) => wells(s).some((w) => {
      if (!isProducer(w)) return false;
      const m = s.fields?.[w.fieldId]?.plots?.[w.plot]?.survey?.methods;
      return Array.isArray(m) && m.length > 0 && m.every((x) => x === 'dowsing');
    }) },
  { id: 'barrels_cost_more', name: 'Barrels Cost More', icon: 'barrel',
    desc: 'Hold 100+ bbl in storage while crude is worth less than the barrel it sits in.',
    check: (s) => {
      if (!s.market || dayToDate(s.day).year >= 1901) return false; // the 1861 glut joke; later eras store oil in steel tanks
      const stored = Object.values(s.fields ?? {}).reduce((a, f) => a + (f.storage?.bbl ?? 0), 0);
      return stored >= 100 && s.market.spot < itemPrice('barrel', dayToDate(s.day).year);
    } },
  { id: 'dry_hole_dan', name: 'Dry Hole Dan', icon: 'dust',
    desc: 'Drill five dry holes in a row. Persistence is a virtue. Probably.',
    check: (s) => {
      if ((s.company?.stats?.dryStreak ?? 0) >= 5) return true;
      // the last five finished wells by spud day (stable: ties keep insertion order) all dry — same answer as sorting
      // every finished well each tick, in O(n) (perf: this runs every tick until unlocked)
      const top = []; // up to 5 of [spudDay, index, well], ascending
      let i = 0;
      for (const w of wells(s)) {
        if (!(isDry(w) || isProducer(w))) continue;
        const k = w.spudDay ?? 0, idx = i++;
        if (top.length === 5 && k < top[0][0]) continue; // ties go later (higher index) → displace
        let j = top.length;
        top.push(null);
        while (j > 0 && top[j - 1][0] > k) { top[j] = top[j - 1]; j--; }
        top[j] = [k, idx, w];
        if (top.length > 5) top.shift();
      }
      return top.length === 5 && top.every((x) => isDry(x[2]));
    } },
  { id: 'ram_it', name: 'Ram It', icon: 'hammer',
    desc: "Drill a well to target on 'Ram it' weight (1.5+) without a single fishing job.",
    check: (s) => wells(s).some((w) => w.depth >= w.target && w.target > 0 && (w.minWob ?? 0) >= 1.5 && !(w.fishingJobs > 0)) },
  // M2 (Gusher & Rotary): src/data/m2/achievements.js
  ...M2_ACHIEVEMENTS,
  // M3 (Postwar & Offshore): src/data/m3/achievements.js
  ...M3_ACHIEVEMENTS,
  // M4 (Shale & Deepwater, locked before 2000): src/data/m4/achievements.js
  ...m4List(M4_ACHIEVEMENTS),
];

// (DESIGN §17) appended by integration
ACHIEVEMENTS.push({ id: 'hired_gun', name: 'Hired Gun', icon: 'derrick', desc: 'Complete five drilling contracts for other operators.', check: (s) => (s.contracts?.stats?.completed ?? 0) >= 5 });

export const ACHIEVEMENT_BY_ID = Object.fromEntries(ACHIEVEMENTS.map((a) => [a.id, a]));
